import { clsx } from 'clsx';
import DangerIconButton from '@/components/buttons/DangerIconButton.tsx';
import { Plus, Trash } from 'lucide-react';
import InputLabel from '@/components/form/base/InputLabel.tsx';
import { ValueInput } from '@/components/form/ValueInput.tsx';
import { UseFieldArrayReturn, useFormContext, get } from 'react-hook-form';
import ActionButton from '@/components/buttons/ActionButton.tsx';
import InputError from '@/components/form/base/InputError.tsx';
import { cn } from '@/lib/utils.ts';
import { BasePriceInputForm } from '@/features/dashboard/tourConfig/schemas/tourPricesSchema.ts';
import { CardContent } from '@/components/ui/Card.tsx';

const AgeRestrictionForm = ({
  currency,
  fieldArray,
  isVariantOnlyForDefinedAgeRanges,
}: {
  currency?: string;
  fieldArray: UseFieldArrayReturn<BasePriceInputForm>;
  isVariantOnlyForDefinedAgeRanges: boolean;
}) => {
  const {
    formState: { errors },
  } = useFormContext<BasePriceInputForm>();

  const { fields, append, remove } = fieldArray;
  const fieldPrefix = 'ageRestrictionPrices';

  return (
    <CardContent className="space-y-1 py-0">
      <div
        className={cn(
          '-mx-4 grid gap-2 bg-neutral-50 px-4 py-2',
          'grid-cols-[1fr_1fr_1fr_1fr_34px]',
        )}
      >
        <InputLabel label={'Nazwa'} />
        <InputLabel label={'Wiek minimalny'} required />
        <InputLabel label={'Wiek maksymalny'} required />
        <InputLabel label={'Koszt'} required />
      </div>
      <div className={clsx('grid gap-2', 'grid-cols-[1fr_1fr_1fr_1fr_34px]')}>
        {fields.map((field, index) => (
          <AgeRestrictionPriceRow
            key={field.id}
            index={index}
            remove={remove}
            currency={currency}
          />
        ))}
      </div>

      {!isVariantOnlyForDefinedAgeRanges && (
        <BasePriceRow currency={currency} />
      )}

      <InputError
        error={
          get(errors, `${fieldPrefix}.message`) ??
          get(errors, `${fieldPrefix}.root.message`)
        }
      />

      <div className="-mx-4 flex justify-start gap-2 border-t border-neutral-200 px-4 py-2">
        <ActionButton
          tone="outline"
          size="xs"
          onClick={() =>
            append({
              name: undefined,
              minAge: undefined,
              maxAge: undefined,
              price: undefined,
            })
          }
        >
          <Plus />
          Dodaj grupę wiekową
        </ActionButton>
      </div>
    </CardContent>
  );
};

const AgeRestrictionPriceRow = ({
  index,
  remove,
  currency,
}: {
  index: number;
  remove: (index: number) => void;
  currency?: string;
}) => {
  const {
    register,
    formState: { errors },
  } = useFormContext<BasePriceInputForm>();

  return (
    <>
      <ValueInput
        register={register(`ageRestrictionPrices.${index}.name`)}
        placeholder="np. Dziecko"
        error={get(errors, `ageRestrictionPrices.${index}.name.message`)}
      />
      <ValueInput
        register={register(`ageRestrictionPrices.${index}.minAge`, {
          valueAsNumber: true,
        })}
        placeholder="0"
        sufix="lat"
        error={get(errors, `ageRestrictionPrices.${index}.minAge.message`)}
      />
      <ValueInput
        register={register(`ageRestrictionPrices.${index}.maxAge`, {
          valueAsNumber: true,
        })}
        placeholder="0"
        sufix="lat"
        error={get(errors, `ageRestrictionPrices.${index}.maxAge.message`)}
      />
      <ValueInput
        register={register(`ageRestrictionPrices.${index}.price`, {
          valueAsNumber: true,
        })}
        placeholder="0"
        sufix={currency}
        error={get(errors, `ageRestrictionPrices.${index}.price.message`)}
      />
      <DangerIconButton onClick={() => remove(index)}>
        <Trash />
      </DangerIconButton>
    </>
  );
};

export default AgeRestrictionForm;

const BasePriceRow = ({ currency }: { currency?: string }) => {
  const {
    register,
    formState: { errors },
  } = useFormContext<BasePriceInputForm>();

  return (
    <div className="grid grid-cols-[1fr_1fr_1fr_1fr_34px] gap-2">
      <ValueInput
        register={register('priceName', { shouldUnregister: true })}
        placeholder="Pozostali"
        error={get(errors, 'priceName.message')}
      />
      <div className="flex items-center text-sm text-base-text">Pozostali</div>
      <div />
      <div className="col-span-1">
        <ValueInput
          register={register('price', {
            valueAsNumber: true,
            shouldUnregister: true,
          })}
          type="number"
          step={0.01}
          placeholder="0"
          sufix={currency}
          error={get(errors, 'price.message')}
        />
      </div>
    </div>
  );
};
